import { useEffect, useRef } from 'react';
import { createGridRow, gridOpacity, GRID_PERIOD, GRID_SQUARE } from './pixel-grid-pattern';
import { useTheme } from './theme';

// The workspace owns its canvas dimensions; auth's column-based PixelField stays independent.
export function WorkspacePattern() {
  const ref = useRef(null);
  const seed = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext('2d', { alpha: true });
    if (!canvas || !context) return;
    const rowSeed = seed.current ?? (seed.current = Math.floor(Math.random() * 10000));
    const node = canvas;
    const ctx = context;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let rows = [];
    let frame = 0;
    let lastFrame = 0;
    const ink = getComputedStyle(node).color;
    const peak = theme === 'dark' ? 0.06 : 0.085;
    function schedule() {
      if (!frame && !document.hidden) frame = requestAnimationFrame(paint);
    }
    function paint(time) {
      frame = 0;
      if (document.hidden) return;
      if (time - lastFrame >= 80 || reduced.matches || lastFrame === 0) {
        lastFrame = time;
        ctx.fillStyle = ink;
        rows.forEach((row, rowIndex) =>
          row.forEach((cell) => {
            const opacity = gridOpacity(cell, time, reduced.matches, peak);
            if (Math.abs(opacity - cell.last) <= 0.003) return;
            const y = rowIndex * GRID_PERIOD;
            ctx.clearRect(cell.x, y, GRID_SQUARE, GRID_SQUARE);
            if (opacity > 0.006) {
              ctx.globalAlpha = opacity;
              ctx.fillRect(cell.x, y, GRID_SQUARE, GRID_SQUARE);
            }
            cell.last = opacity;
          }),
        );
        ctx.globalAlpha = 1;
      }
      if (!reduced.matches) schedule();
    }
    function resize() {
      const width = Math.max(1, Math.ceil(node.clientWidth));
      const height = Math.max(1, Math.ceil(node.clientHeight));
      if (rows.length && node.width === width && node.height === height) return;
      node.width = width;
      node.height = height;
      rows = Array.from({ length: Math.ceil(height / GRID_PERIOD) }, (_, row) =>
        createGridRow(Math.ceil(width / GRID_PERIOD), row + rowSeed),
      );
      lastFrame = 0;
      schedule();
    }
    function syncMotion() {
      cancelAnimationFrame(frame);
      frame = 0;
      rows.forEach((row) =>
        row.forEach((cell) => {
          cell.last = -1;
        }),
      );
      lastFrame = 0;
      schedule();
    }
    function syncVisibility() {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else schedule();
    }
    const observer = new ResizeObserver(resize);
    observer.observe(node);
    resize();
    reduced.addEventListener('change', syncMotion);
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      reduced.removeEventListener('change', syncMotion);
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, [theme]);

  return <canvas ref={ref} className="dashboard-pattern" aria-hidden="true" />;
}
